import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, ChevronRight, Download } from "lucide-react";
import { atlasFetch, keyMode } from "@/lib/atlas-api";
import { downloadCsv } from "@/lib/atlas-csv";
import { Card, Skeleton, EmptyState, PrimaryButton, GhostButton } from "@/components/portal/PortalPrimitives";

function fmtMoney(n) {
  return (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

const CURL_EXAMPLE = `curl -X POST https://atlasforgefinancial.com/api/v1/ingest \\
  -H "X-API-Key: $ATLAS_KEY" \\
  -H "X-Atlas-Ingest-Secret: $ATLAS_INGEST_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "end_user_id": "usr_demo",
    "cash_balance": 4218.50,
    "upcoming_bills": [],
    "savings_commitments": 0,
    "debt_commitments": 0
  }'`;

export default function PortalUsers() {
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [history, setHistory] = useState([]); // for back navigation
  const [count, setCount] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchPage = async (c = null) => {
    const qs = new URLSearchParams({ limit: "100" });
    if (c) qs.set("cursor", c);
    const r = await atlasFetch(`/api/v1/ingest/calculations?${qs.toString()}`, { needsIngestSecret: true });
    setItems(r.items || []);
    setCursor(r.next_cursor || null);
    setCount(r.count ?? (r.items || []).length);
  };

  useEffect(() => {
    fetchPage().catch((e) => {
      toast.error(e.message);
      setItems([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goNext = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      setHistory((h) => [...h, cursor]);
      await fetchPage(cursor);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const goBack = async () => {
    if (history.length === 0) return;
    setLoadingMore(true);
    try {
      const prev = [...history];
      prev.pop();
      setHistory(prev);
      const target = prev.length === 0 ? null : prev[prev.length - 1];
      await fetchPage(target);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const doDelete = async (userId) => {
    try {
      await atlasFetch(`/api/v1/ingest/user/${encodeURIComponent(userId)}`, {
        method: "DELETE",
        needsIngestSecret: true,
      });
      toast.success(`Deleted end-user ${userId}`);
      setConfirmDelete(null);
      setSelected(null);
      const last = history.length === 0 ? null : history[history.length - 1];
      await fetchPage(last);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">End-users</p>
          <h1 className="font-display text-[28px] text-[#0A1128] mt-1.5">Cached calculations</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {count != null && (
            <span data-testid="users-count" className="text-[12px] text-slate-500">{count} on this page</span>
          )}
          <button
            data-testid="users-download-csv"
            onClick={async () => {
              try {
                const envTag = keyMode() === "production" ? "production" : "sandbox";
                await downloadCsv({
                  path: "/api/v1/reports/calculations.csv",
                  filename: `atlas-${envTag}-linked-accounts-${new Date().toISOString().slice(0,10)}.csv`,
                  needsIngest: true,
                });
                toast.success("Linked accounts CSV downloaded");
              } catch (e) {
                toast.error(e.message);
              }
            }}
            className="inline-flex items-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[12.5px] font-medium px-4 py-2 hover:bg-[#0E2A20] transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Download CSV
          </button>
        </div>
      </div>

      {items === null && (
        <Card>
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="mt-3 h-4 w-3/4" />
          <Skeleton className="mt-3 h-4 w-2/3" />
          <Skeleton className="mt-3 h-4 w-5/6" />
        </Card>
      )}

      {items && items.length === 0 && (
        <EmptyState
          testId="users-empty"
          title="No users yet."
          hint="Push your first calculation via POST /api/v1/ingest to populate this table."
        >
          <pre className="text-left mx-auto max-w-2xl bg-slate-900 text-slate-100 text-[12px] rounded-[10px] p-4 overflow-x-auto">{CURL_EXAMPLE}</pre>
          <button
            data-testid="users-copy-curl"
            onClick={() => {
              navigator.clipboard.writeText(CURL_EXAMPLE);
              toast.success("Copied curl example");
            }}
            className="mt-3 text-[12.5px] text-[#1A4232] underline"
          >
            Copy
          </button>
        </EmptyState>
      )}

      {items && items.length > 0 && (
        <>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="users-table">
                <thead className="bg-[#F5F1EB]/70 text-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">End-user</th>
                    <th className="text-left px-4 py-3 font-semibold">Email</th>
                    <th className="text-right px-4 py-3 font-semibold">Safe-to-Spend</th>
                    <th className="text-right px-4 py-3 font-semibold">Daily</th>
                    <th className="text-left px-4 py-3 font-semibold">Calculated</th>
                    <th className="text-right px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.end_user_id}
                      data-testid={`users-row-${row.end_user_id}`}
                      className="border-t border-slate-100 hover:bg-[#F5F1EB]/30 cursor-pointer"
                      onClick={() => setSelected(row)}
                    >
                      <td className="px-4 py-3 font-mono text-[12.5px] text-[#0A1128]">{row.end_user_id}</td>
                      <td className="px-4 py-3 text-slate-700">{row.user_email || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#0A1128]">{fmtMoney(row.calculation?.safe_to_spend_today)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">{fmtMoney(row.calculation?.daily_allowance)}</td>
                      <td className="px-4 py-3 text-slate-500 text-[12px]">{fmtDate(row.calculated_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(row.end_user_id);
                          }}
                          data-testid={`users-delete-${row.end_user_id}`}
                          className="inline-flex items-center gap-1.5 text-rose-700 text-[12.5px] hover:text-rose-900"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          GDPR delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="flex items-center justify-between">
            <GhostButton testId="users-prev" onClick={goBack} disabled={history.length === 0 || loadingMore}>
              ← Previous
            </GhostButton>
            <span className="text-[12px] text-slate-500">page {history.length + 1}</span>
            <GhostButton testId="users-next" onClick={goNext} disabled={!cursor || loadingMore}>
              Next <ChevronRight className="h-3.5 w-3.5" />
            </GhostButton>
          </div>
        </>
      )}

      {/* End-user detail */}
      {selected && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4" onClick={() => setSelected(null)}>
          <div className="w-full max-w-xl rounded-[12px] bg-white shadow-2xl border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
            <div className="p-6 border-b border-slate-100 flex items-baseline justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">End-user</p>
                <p className="font-mono text-[15px] text-[#0A1128]">{selected.end_user_id}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700 text-[20px]">×</button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-y-3 gap-x-6 text-[13px]">
              <div className="col-span-2 grid grid-cols-2 gap-3">
                <div className="rounded-[10px] bg-[#1A4232] text-[#F5F1EB] p-4">
                  <p className="text-[10.5px] uppercase tracking-[0.18em] opacity-70">Safe to spend today</p>
                  <p className="font-display text-[24px] mt-1">{fmtMoney(selected.calculation?.safe_to_spend_today)}</p>
                </div>
                <div className="rounded-[10px] bg-[#F5F1EB] text-[#1A4232] p-4">
                  <p className="text-[10.5px] uppercase tracking-[0.18em] opacity-80">Daily allowance</p>
                  <p className="font-display text-[24px] mt-1">{fmtMoney(selected.calculation?.daily_allowance)}</p>
                </div>
              </div>
              <Row label="Monthly safe-to-spend" value={fmtMoney(selected.calculation?.monthly_safe_to_spend)} />
              <Row label="Cash after buffer" value={fmtMoney(selected.calculation?.breakdown?.cash_after_buffer)} />
              <Row label="Upcoming bills" value={fmtMoney(selected.calculation?.breakdown?.upcoming_bills_total)} />
              <Row label="Savings commitments" value={fmtMoney(selected.calculation?.breakdown?.savings_commitments)} />
              <Row label="Debt commitments" value={fmtMoney(selected.calculation?.breakdown?.debt_commitments)} />
              <Row label="Discretionary pool" value={fmtMoney(selected.calculation?.breakdown?.discretionary_pool)} />
              <Row label="Days in period" value={selected.calculation?.breakdown?.days_in_period ?? "—"} />
              <Row label="Calculation id" value={<span className="font-mono text-[12px]">{selected.calculation?.calculation_id}</span>} />
              <Row label="Calculated at" value={fmtDate(selected.calculated_at)} />
            </div>
            <div className="p-5 border-t border-slate-100 flex justify-between">
              <button
                onClick={() => setConfirmDelete(selected.end_user_id)}
                className="text-rose-700 text-[13px] inline-flex items-center gap-2"
              >
                <Trash2 className="h-3.5 w-3.5" /> GDPR delete this user
              </button>
              <GhostButton onClick={() => setSelected(null)}>Close</GhostButton>
            </div>
          </div>
        </div>
      )}

      {/* GDPR delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4" data-testid="gdpr-confirm">
          <div className="w-full max-w-md rounded-[12px] bg-white shadow-2xl border border-slate-200 p-6">
            <p className="font-display text-[18px] text-[#0A1128]">GDPR delete?</p>
            <p className="mt-2 text-[13px] text-slate-600">
              This permanently removes all cached calculations for{" "}
              <span className="font-mono text-[#0A1128]">{confirmDelete}</span>. Cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <GhostButton onClick={() => setConfirmDelete(null)}>Cancel</GhostButton>
              <button
                data-testid="gdpr-confirm-btn"
                onClick={() => doDelete(confirmDelete)}
                className="rounded-full bg-rose-600 text-white text-[13px] font-medium px-5 py-2 hover:bg-rose-700"
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <>
      <div className="text-slate-500">{label}</div>
      <div className="text-right text-[#0A1128] tabular-nums">{value}</div>
    </>
  );
}
